import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  Inject,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';

import { DOCUMENT } from '@angular/common';

import { ModalSharedService } from './modal.sharedService';
import { getFocusableElements, setFocusOnFirstFocusableElement, circulateFocus } from 'axis-components/components/utilities/utilities';

@Component({
  selector: 'axis-modal-header',
  template: `<ng-content></ng-content>`
})
export class ModalHeaderComponent { }

@Component({
  selector: 'axis-modal-content',
  template: `<ng-content></ng-content>`
})
export class ModalContentComponent { }

@Component({
  selector: 'axis-modal-footer',
  template: `<div #modalFooter class="axis-modal-footer"><ng-content></ng-content></div>`
})
export class ModalFooterComponent { }

@Component({
  selector: 'axis-modal',
  templateUrl: './modal.component.html'
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  public axisHeaderFlag = true;
  public heightNumber: number;
  public isOpened = false;
  public maxHeight: number;
  public minHeight: number;
  public modalHeight: number;
  public modalBodyHeight: number;
  public modalInstance: any;
  openButton: HTMLElement;
  public uniqueKey: number;

  @Input() public ariaLabel = '';
  @Input() public ariaLabelledby: string;
  @Input() public ariaDescribedby: string;
  @Input() public modalRole = 'dialog';
  @Input() public animation = true;
  @Input() backdrop: string | boolean = 'static'; // this input property is of no use we can later delete this.
  @Input() public closeOnEscape = false;
  @Input() public closeOnOutsideClick = false;
  @Input() public closeButton = false;
  @Input() public cancelButtonLabel: string;
  @Input() font = 'custom';
  @Input() heading: string;
  @Input() height: string;
  // tslint:disable-next-line: no-input-rename
  @Input('data-size') public size: string; // to apply css in ng9, data-size should be given as input
  @Input() public submitButtonLabel: string;
  @Input() public top: string;
  @Input('data-top') // property added in ng9 version to support existing applications using data-top.
  get dataTop() {
    return this.top;
  }
  set dataTop(value) {
    this.top = value;
  }

  @Output() public onBeforeClose = new EventEmitter(false);
  @Output() public onClose = new EventEmitter(false);
  @Output() public onOpen = new EventEmitter(false);
  @Output() public onSubmit = new EventEmitter(false);

  @ViewChild('modalRoot', { static: false }) public modalRoot: ElementRef;
  @ViewChild('modalHeading', { static: false }) public modalHeading: ElementRef;
  @ViewChild('modalBody', { static: false }) public modalBody: ElementRef;
  @ViewChild('modalWrapper', { static: false }) public modalWrapper: ElementRef;

  @ContentChild(ModalFooterComponent, { read: ElementRef, static: true }) public modalFooter: any;

  @HostBinding('attr.data-keyboard')
  get dataKeyboardAttr(): boolean {
    return false;
  }

  constructor(private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector,
    private modalSharedService: ModalSharedService,
    @Inject(DOCUMENT) private _document: any,
    private renderer: Renderer2) { }

  ngAfterViewInit() {
    if (this.modalHeading && this.modalHeading.nativeElement) {
      if (this.modalHeading.nativeElement.children.length === 0) {
        this.axisHeaderFlag = false;
        this.changeDetectorRef.detectChanges();
      }
      if (this.ariaLabel === '' && this.modalHeading && !this.closeButton) {
        this.ariaLabel = this.modalHeading.nativeElement.innerText;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  ngOnDestroy() {
    this._document.body.classList.remove('axis-modal-open');
  }

  /**
   * handle focus to circulate within modal container.
   * @param event as KeyboardEvent
   */
  handleFocus(event: KeyboardEvent) {
    const focusableElements: HTMLElement[] = getFocusableElements(this.modalWrapper);
    circulateFocus(event, focusableElements, this._document, true, 'axis-modal-wrapper');
  }

  /**
   * API method to open the modal.
   * This will open the modal, set focus on first element inside modal, and store the parent focus to set back after modal close.
   * @param args as any[]
   */
  open(...args: any[]) {
    if (this.isOpened) {
      return;
    }
    this.openButton = this._document.activeElement as HTMLElement;
    this.isOpened = true;
    this.onOpen.emit(args);

    window.setTimeout(() => {
      const focusableElements: HTMLElement[] = getFocusableElements(this.modalWrapper);
      if (focusableElements.length) {
        setFocusOnFirstFocusableElement(focusableElements);
      } else { // if modal is empty, then set focus on modal and preventDefault on tab press to restrict tab focusing outside modal.
        this.modalRoot.nativeElement.focus();
        this.renderer.listen(this.modalRoot.nativeElement, 'keydown', (event) => {
          const key = event.key.toLocaleLowerCase();
          if (key === 'tab') {
            event.preventDefault();
          }
        });
      }
      this.calculateModalHeight();
    }, 0);
    // To Do find alternate way
    this._document.body.classList.add('axis-modal-open');
  }

  /**
   * API method to close the modal.
   * This will close the modal, set focus parent element and delete the modal instances.
   * @param args as any[]
   */
  close(...args: any[]) {
    if (!this.isOpened) {
      return;
    }
    this.onBeforeClose.emit(args);
    this.isOpened = false;
    this.onClose.emit(args);
    // To Do find alternate way

    this.modalInstance = this.modalSharedService['modalInstance'][this.uniqueKey];
    this._document.body.classList.remove('axis-modal-open');
    if (this.modalInstance && this.modalInstance.componentRef) {
      const modalElement = this.modalInstance.componentRef.location.nativeElement;
      if (modalElement && modalElement.parentNode) {
        modalElement.parentNode.removeChild(modalElement);
      }
      this.modalInstance.componentRef.destroy();
      this.modalInstance = null;
      this.modalSharedService.destroyInstance(this.uniqueKey);
    } else {
      if (this._document.body.querySelector('.tooltip-container')) {
        this._document.body.removeChild(this._document.body.querySelector('.tooltip-container'));
      }
    }

    if (this.openButton) {
      this.openButton.focus();
    }
  }

  calculateModalHeight() {
    let minHeight = 0;
    if (this.height) {
      minHeight = parseInt(this.height, 10);
    }
    let maxHeight;
    let headerHeight = 0;
    if (this.heightNumber) {
      minHeight = this.heightNumber;
    }
    const windowHeight = window.innerHeight;
    if (this.axisHeaderFlag) {
      headerHeight = this.modalHeading.nativeElement.offsetHeight;
    }
    if (this.modalFooter) {
      maxHeight =
        windowHeight -
        headerHeight -
        this.modalFooter.nativeElement.children[0].offsetHeight -
        2 * this.modalRoot.nativeElement.firstElementChild.offsetTop;
    } else {
      maxHeight =
        windowHeight -
        headerHeight -
        2 * this.modalRoot.nativeElement.firstElementChild.offsetTop;
    }
    minHeight = Math.min(minHeight, maxHeight);

    this.maxHeight = maxHeight;
    this.minHeight = minHeight;
  }

  public preventClosing(event: MouseEvent) {
    event.stopPropagation();
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isOpened) {
      this.calculateModalHeight();
    }
  }

  onOutsideClick(event: Event) {
    if (!event['path'].includes(this.modalWrapper.nativeElement)) {
      this.close();
    }
  }
}
